import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Eye, EyeOff, Loader2, CheckCircle } from 'lucide-react';

export function SignUpPage() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) {
        navigate('/dashboard');
      }
    });
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setLoading(true);

    const fullName = `${firstName} ${lastName}`.trim();

    const { error } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: {
        data: {
          full_name: fullName,
        },
        emailRedirectTo: "https://talisonservices.co.uk/dashboard"
      }
    });

    if (error) {
      setError(error.message);
      setLoading(false);
      return;
    }

    setSuccess('Account created! Please check your email to verify.');
    setLoading(false);
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-8 px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 sm:p-10">

          <div className="text-center mb-8">
            <Link to="/" className="text-2xl font-bold text-nhs-blue">
              TALIS'ON SERVICES
            </Link>
            <h2 className="mt-4 text-xl font-semibold text-gray-900">
              Join Talis'on Services
            </h2>
            <p className="mt-2 text-sm text-gray-500">
              Create your account to get started
            </p>
          </div>

        {error && <div className="bg-red-50 p-3 rounded text-red-600 mb-4">{error}</div>}
        {success && (
          <div className="bg-green-50 p-3 rounded text-green-600 mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            {success}
          </div>
        )}

        {!success && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="text"
              placeholder="First and Middle Name"
              required
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-nhs-blue focus:border-transparent"
            />
            <input
              type="text"
              placeholder="Last Name"
              required
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-nhs-blue focus:border-transparent"
            />
            <input
              type="email"
              placeholder="Your@email.com"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-nhs-blue focus:border-transparent"
            />
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-nhs-blue focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-3"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirm Password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-nhs-blue focus:border-transparent"
            />

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-nhs-blue text-white py-3 rounded flex justify-center items-center gap-2 rounded-lg"
            >
              {loading ? <Loader2 className="animate-spin w-4 h-4" /> : 'Create Account'}
            </button>
            <div className="text-center mt-6 text-sm">
          <Link to="/forgot-password" className="text-nhs-blue">
            Forgot password?
          </Link>
        </div>
          </form>
        )}

        <div className="text-center mt-6 text-sm">
          Already have an account?{' '}
          <Link to="/login" className="text-nhs-blue">Sign in</Link>
        </div>
      </div>
    </div>
  </div>
  );
}